<script lang="ts" setup>
import { ref, computed, watchEffect } from 'vue';
import { getImageUrl } from '@/presentation/utils';

interface Props {
	src?: string;
	srcType?: 'public' | 'api' | 'fullPath';
	noPlaceHolder?: boolean;
	lightbox?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	srcType: 'api',
	lightbox: false,
});

const fallbackSrc = 'images/default-image.jpg';
const imageSrc = ref<string | undefined>(fallbackSrc);

const sourceResolvers: Record<string, (src: string) => string> = {
	public: (src) => src,
	api: (src) => getImageUrl(src),
	fullPath: (src) => src,
};

const resolveImageSource = computed<string>({
	get() {
		if (!imageSrc.value) return fallbackSrc;
		const resolver = sourceResolvers[props.srcType];
		return resolver ? resolver(imageSrc.value) : fallbackSrc;
	},
	set(value: string) {
		imageSrc.value = value;
	},
});

watchEffect(() => {
	resolveImageSource.value = props.src;
});

const handleImageError = () => {
	resolveImageSource.value = fallbackSrc;
};

const isOpen = ref(false);
const openLightbox = () => {
	if (props.lightbox) isOpen.value = true;
};
const closeLightbox = () => {
	isOpen.value = false;
};
</script>

<template>
	<img
		v-bind="$attrs"
		:src="resolveImageSource"
		loading="lazy"
		@error="handleImageError"
		@click="openLightbox"
		:class="{ 'cursor-zoom-in': props.lightbox }"
		alt="Image"
	/>

	<teleport to="body">
		<div v-if="isOpen" class="fixed inset-0 w-screen h-screen bg-black bg-opacity-80 flex items-center justify-center z-[10000]" @click.self="closeLightbox">
			<img :src="resolveImageSource" alt="Full Image" class="max-w-[90vw] max-h-[90vh] object-contain cursor-auto" @error="handleImageError" />
			<button class="fixed top-4 right-4 bg-transparent border-0 text-white text-[2rem] cursor-pointer" @click="closeLightbox">×</button>
		</div>
	</teleport>
</template>
